import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Buildings, Truck, UserCircle, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [role, setRole] = useState("company");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [sendingForgot, setSendingForgot] = useState(false);

  const sendForgot = async () => {
    if (!forgotEmail.includes("@")) { toast.error("Digite um e-mail válido"); return; }
    setSendingForgot(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      toast.success("E-mail enviado! Verifique sua caixa (e a pasta de Spam).");
      setForgotOpen(false);
      setForgotEmail("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao solicitar reset");
    } finally { setSendingForgot(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Try selected role first, then fall back to the other roles automatically
      // so login works regardless of which tab user picked.
      const primary = role === "motorista" ? ["driver", "autonomo"] : [role];
      const fallback = role === "motorista" ? ["company"] : ["driver", "autonomo"];
      const tryRoles = [...primary, ...fallback];
      let data = null, lastErr = null;
      for (const r of tryRoles) {
        try {
          const res = await api.post("/auth/login", { email, password, role: r });
          data = res.data;
          break;
        } catch (err) { lastErr = err; }
      }
      if (!data) throw lastErr;
      login(data.token, data.user);
      toast.success(`Bem-vindo, ${data.user.name || data.user.company_name}!`);
      const target = data.user.role === "company" ? "/empresa" : data.user.role === "autonomo" ? "/autonomo" : "/motorista";
      nav(target);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no login");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative gradient-blue">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Logo size={56} className="text-white [&_div]:!text-white [&_div>div]:!text-blue-200" />
          <div>
            <h1 className="text-5xl font-extrabold leading-[1.05] tracking-tight" style={{ fontFamily: "Manrope" }}>
              Acerto de viagens<br />
              <span className="text-yellow-300">em minutos.</span>
            </h1>
            <p className="mt-6 text-lg text-blue-100 max-w-md">
              Chegou na empresa? Faça o acerto na hora. Motorista lança os dados, empresa acompanha em tempo real.
            </p>
            <div className="mt-6 rounded-lg overflow-hidden border-2 border-blue-300/40 shadow-2xl bg-black">
              <video src="/promo.mp4" autoPlay muted loop playsInline data-testid="promo-video" className="w-full aspect-video object-cover" />
            </div>
            <div className="mt-6 flex gap-6 text-sm text-blue-100">
              <div><span className="text-yellow-300 font-bold text-2xl block">7 dias</span>grátis</div>
              <div><span className="text-yellow-300 font-bold text-2xl block">150</span>veículos/empresa</div>
              <div><span className="text-yellow-300 font-bold text-2xl block">Offline</span>funciona sem internet</div>
            </div>
          </div>
          <div className="text-xs text-blue-200/80">© 2026 Acerto Rápido JVO</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        <Card className="w-full max-w-md p-6 md:p-8 border-2">
          {/* Mobile hero — visible only on small screens */}
          <div className="lg:hidden mb-4 -mx-2">
            <div className="rounded-xl overflow-hidden gradient-blue p-5 text-white text-center relative">
              <div className="absolute inset-0 grid-bg opacity-30" />
              <div className="relative">
                <div className="flex justify-center mb-2"><Logo size={56} /></div>
                <h1 className="text-2xl font-extrabold tracking-tight leading-tight" style={{ fontFamily: "Manrope" }}>
                  Acerto de viagens<br /><span className="text-yellow-300">em minutos</span>
                </h1>
                <p className="text-xs text-blue-100 mt-1.5">Motorista lança os dados. Empresa vê em tempo real. Acerto pronto ao chegar.</p>
                <div className="mt-3 flex justify-center gap-3 text-[11px] text-blue-100">
                  <span className="bg-white/15 px-2 py-1 rounded"><b className="text-yellow-300">7 dias</b> grátis</span>
                  <span className="bg-white/15 px-2 py-1 rounded"><b className="text-yellow-300">Offline</b> funciona</span>
                </div>
              </div>
            </div>
            <Link to="/register" data-testid="mobile-cta-register" className="block mt-4 mx-2 bg-yellow-400 hover:bg-yellow-300 text-blue-900 font-extrabold text-center py-3 rounded-lg shadow-md transition-colors">
              🚀 CRIAR CONTA GRÁTIS — TESTAR AGORA
            </Link>
            <p className="text-center text-xs text-slate-500 mt-2 mb-4">
              Ou entre com sua conta abaixo ⬇️
            </p>
          </div>

          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Entrar</h2>
          <p className="text-sm text-muted-foreground mt-1">Acesse sua conta</p>

          <Tabs value={role} onValueChange={setRole} className="mt-6">
            <TabsList className="grid grid-cols-2 w-full h-auto p-1">
              <TabsTrigger value="company" data-testid="tab-role-company" className="py-2.5 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs">
                <Buildings size={16} weight="duotone" /> Empresa
              </TabsTrigger>
              <TabsTrigger value="motorista" data-testid="tab-role-motorista" className="py-2.5 gap-1.5 data-[state=active]:bg-blue-600 data-[state=active]:text-white text-xs">
                <Truck size={16} weight="duotone" /> Motorista
              </TabsTrigger>
            </TabsList>
            <TabsContent value={role}>
              <form onSubmit={submit} className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider">E-mail</Label>
                  <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5" />
                </div>
                <div>
                  <Label htmlFor="password" className="text-xs uppercase tracking-wider">Senha</Label>
                  <div className="relative mt-1.5">
                    <Input id="password" data-testid="login-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required className="pr-10" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} data-testid="toggle-password-visibility" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-800 p-1" aria-label={showPassword ? "Esconder senha" : "Mostrar senha"}>
                      {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                  <button type="button" onClick={() => { setForgotEmail(email); setForgotOpen(true); }} data-testid="link-forgot-password" className="text-xs text-blue-700 hover:underline mt-1.5 block">
                    Esqueci minha senha
                  </button>
                </div>
                <Button type="submit" data-testid="login-submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700 hover:text-white text-white h-11">
                  {busy ? "Entrando..." : "Entrar"}
                </Button>
                <p className="text-sm text-center text-muted-foreground">
                  Não tem conta?{" "}
                  <Link to="/register" data-testid="link-register" className="text-blue-700 font-semibold hover:underline">Cadastre-se</Link>
                </p>
              </form>
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="max-w-sm max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Esqueci minha senha</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Digite seu e-mail cadastrado. Vamos te enviar um link pra criar uma nova senha.</p>
            <div>
              <Label>E-mail</Label>
              <Input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="voce@email.com" data-testid="forgot-email-input" autoFocus />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForgotOpen(false)}>Cancelar</Button>
            <Button onClick={sendForgot} disabled={sendingForgot} data-testid="btn-send-forgot" className="bg-blue-600 hover:bg-blue-700 text-white">
              {sendingForgot ? "Enviando..." : "Enviar link"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
